<script lang="ts">
	import { mount, onDestroy, onMount } from 'svelte';
	import type { Unset } from '@otto-ec/global-resources/nexus';
	import { getTeaserAndPointsInfoBannerContext } from './DetailViewInfoTeaserClient.ts';
	import type VariationChangedEvent from '../../../domain/VariationChangedEvent.ts';
	import type BenefitToggledEvent from '../../../domain/points-info-banner/BenefitToggledEvent.ts';
	import { TeaserType } from '../../../domain/teaser/TeaserType.ts';
	import CombinedInfoTeaser from '../../../components/combined-info-teaser/CombinedInfoTeaser.svelte';
	import { PointsInfoBannerType } from '../../../domain/points-info-banner/PointsInfoBannerType.ts';
	import { FreeShippingBannerType } from '../../../domain/free-shipping-banner/FreeShippingBannerType.ts';
	import Banner from '../../../components/points-info-banner/Banner.svelte';
	import ToSignUp from '../../../components/teaser/detailView/ToSignUp.svelte';
	import ToBooster from '../../../components/teaser/detailView/ToBooster.svelte';
	import { logger } from '../../../logger.ts';
	import FreeShippingBanner from '../../../components/free-shipping-banner/FreeShippingBanner.svelte';
	import Divider from '../../../elements/Divider.svelte';
	import ToNonUpPlusBooster from '../../../components/teaser/detailView/ToNonUpPlusBooster.svelte';

	let handleVariationChangeRef: Unset;
	let targetElement: HTMLElement;
	let isVariationChangeHandlerFinish: boolean = false;

	const CHECK_FREQUENCY = 100;
	const TIMEOUT = 4000;

	function mountComponents(
		targetElement: HTMLElement,
		teaserType: TeaserType,
		teaserContent: any,
		pointsInfoBannerType: PointsInfoBannerType,
		pointsInfoBannerContent: any,
		freeShippingBannerType: FreeShippingBannerType,
		showBoosterCampaignRedesign: boolean
	) {
		const isUnidentifiedUser = (teaserType === TeaserType.TO_LOGIN || pointsInfoBannerType === PointsInfoBannerType.TO_LOGIN);
		const isNonMember = (teaserType === TeaserType.BASIC_AND_PLUS_TEASER || pointsInfoBannerType === PointsInfoBannerType.JOIN_BASIC_AND_PLUS);
		const showPointsInfoBanner = pointsInfoBannerType != PointsInfoBannerType.NO_CONTENT && pointsInfoBannerContent != undefined;
		const showBoosterTeaser = teaserType === TeaserType.BOOSTER_TEASER;
		const showNonUpPlusBoosterTeaser = teaserType === TeaserType.NON_UP_PLUS_BOOSTER_TEASER && showBoosterCampaignRedesign && teaserContent != undefined;
		const showCombinedInfoTeaser = (isUnidentifiedUser || isNonMember) && teaserType !== TeaserType.NON_UP_PLUS_BOOSTER_TEASER;
		const showTeaser = (teaserType != TeaserType.NO_CONTENT && teaserType != TeaserType.NON_UP_PLUS_BOOSTER_TEASER) && teaserContent != undefined;
		const showFreeShippingBanner = freeShippingBannerType === FreeShippingBannerType.FREE_SHIPPING_INCLUDED;

		if (showCombinedInfoTeaser) {
			mount(CombinedInfoTeaser, {
				target: targetElement,
				props: {
					teaserType: teaserType,
					pointsInfoBannerType: pointsInfoBannerType,
					pointsInfoBannerContent: pointsInfoBannerContent,
					teaserContent: teaserContent
				}
			});
		} else if (showNonUpPlusBoosterTeaser) {
			mount(ToNonUpPlusBooster, {
				target: targetElement,
				props: {
					teaserContent: teaserContent
				}
			});
		} else {
			if (showFreeShippingBanner) {
				mount(FreeShippingBanner, {
					target: targetElement
				});
			}
			if (showFreeShippingBanner && (showPointsInfoBanner || showTeaser)) {
				mount(Divider, {
					target: targetElement
				})
			}
			if (showPointsInfoBanner) {
				mount(Banner, {
					target: targetElement,
					props: {
						content: pointsInfoBannerContent,
						type: pointsInfoBannerType
					}
				});
			}
			if (showTeaser) {
				if (showBoosterTeaser) {
					mount(ToBooster, {
						target: targetElement,
						props: {
							teaserContent: teaserContent
						}
					});
				} else {
					mount(ToSignUp, {
						target: targetElement,
						props: {
							teaserContent: teaserContent,
							teaserType: teaserType
						}
					});
				}
			}
		}
	}

	async function fetchTeaserContent(event: VariationChangedEvent) {
		if (event.cdc) {
			logger.info(`Handling CDC test for variationID ${event.variationId}`);
			event.targetElement.innerHTML = event.variationId;
		} else {
			const {
				teaserType,
				teaserContent,
				pointsInfoBannerType,
				pointsInfoBannerContent,
				freeShippingBannerType,
				showBoosterCampaignRedesign
			} = await getTeaserAndPointsInfoBannerContext(event.variationId);

			targetElement = event.targetElement;
			mountComponents(event.targetElement, teaserType, teaserContent, pointsInfoBannerType, pointsInfoBannerContent, freeShippingBannerType, showBoosterCampaignRedesign);
		}
		isVariationChangeHandlerFinish = true;
	}

	const handleBenefitToggleEvent = async (event: BenefitToggledEvent) => {
		const { variationId, id: benefitId } = event;
		let isBenefitChangeHandlerInProgress = false;

		let intervalId = setInterval(async () => {
			if (isVariationChangeHandlerFinish && !isBenefitChangeHandlerInProgress) {
				isBenefitChangeHandlerInProgress = true;
				await handleBenefitChange(intervalId, variationId, benefitId);
				isBenefitChangeHandlerInProgress = false;
			}
		}, CHECK_FREQUENCY);

		setTimeout(() => {
			clearInterval(intervalId);
		}, TIMEOUT);
	};

	const handleBenefitChange = async (intervalId: any, variationId: string, benefitId: string) => {
		logger.info(`Handling benefit toggled event with variation ${variationId} and benefit ${benefitId}`);
		const {
			teaserType,
			teaserContent,
			pointsInfoBannerType,
			pointsInfoBannerContent,
			freeShippingBannerType,
			showBoosterCampaignRedesign
		} = await getTeaserAndPointsInfoBannerContext(variationId, benefitId);

		if (!targetElement || (teaserType === TeaserType.NO_CONTENT && pointsInfoBannerType === PointsInfoBannerType.NO_CONTENT)) {
			return;
		}

		targetElement.innerHTML = '';
		mountComponents(targetElement, teaserType, teaserContent, pointsInfoBannerType, pointsInfoBannerContent, freeShippingBannerType, showBoosterCampaignRedesign);
		isVariationChangeHandlerFinish = false;
		clearInterval(intervalId);
	};

	onMount(async () => {
		const { articleInfoTeaserVariationChange } = await import ('@otto-ec/nexus-registry/promo/up/teaser');
		const { benefitEvents } = await import ('@otto-ec/nexus-registry/benefit/events');

		handleVariationChangeRef = articleInfoTeaserVariationChange.assignFunction(fetchTeaserContent);
		benefitEvents.active.on(handleBenefitToggleEvent);
	});

	onDestroy(() => {
		if (handleVariationChangeRef) {
			handleVariationChangeRef();
		}
	});
</script>
